// ============================================================================
// smartSandboxConsole.js — SMART Build 2K (pure; sandbox launch console model)
//
// Turns a tenant EHR connection + safe mapping summary into the copyable values
// and verification checklists an admin needs to run a real SMART sandbox launch.
//
// This is an OPERATOR CONSOLE, not a replacement EHR. It produces:
//   - the exact Launch URL / Redirect URI / scopes to register externally,
//   - safe connection-mode + mapping-readiness summaries (no secrets, no PHI),
//   - baseline / enforced test checklists + a post-launch checklist + failure guide.
//
// It NEVER emits secrets, tokens, patient context, raw fhirUser, or fhirUserHash.
// The real launch still starts at /ehr/smart/launch via an external SMART launcher.
// ============================================================================

// Canonical hosted app domain (must match the EHR-registered URLs + REFERRALGATE_HOSTS).
export const SMART_APP_HOST = 'https://referral-gate.web.app';
export const SMART_LAUNCH_PATH = '/ehr/smart/launch';
export const SMART_CALLBACK_PATH = '/ehr/smart/callback';
export const SMART_REQUIRED_SCOPES = 'launch openid fhirUser patient/Patient.r patient/Coverage.rs';
export const SMART_APP_LAUNCHER_URL = 'https://launch.smarthealthit.org/';

// Roles allowed to see/use the sandbox console.
const SANDBOX_CONSOLE_ROLES = new Set(['platform_admin', 'tenant_admin']);

/** Visibility gate — platform_admin + tenant_admin only (no GP / non-admin). */
export function canUseSandboxConsole(role) {
  return SANDBOX_CONSOLE_ROLES.has(role);
}

function trimHost(host) {
  return String(host || SMART_APP_HOST).replace(/\/+$/, '');
}

/**
 * Exact Launch URL to register in the external SMART launcher / EHR. The EHR
 * appends `iss` + `launch` at launch time. Returns null if ids are missing.
 */
export function buildSmartLaunchUrl({ tenantId, connectionId, host = SMART_APP_HOST } = {}) {
  if (!tenantId || !connectionId) return null;
  return `${trimHost(host)}${SMART_LAUNCH_PATH}?tenantId=${encodeURIComponent(tenantId)}&connectionId=${encodeURIComponent(connectionId)}`;
}

/** Exact Redirect URI to register (the SMART callback). */
export function buildSmartRedirectUri(host = SMART_APP_HOST) {
  return `${trimHost(host)}${SMART_CALLBACK_PATH}`;
}

/**
 * OPTIONAL dev helper — a manual launch URL for already-known inputs. This is NOT
 * a real launch: `launch` must come from a real SMART launcher/EHR for the token
 * endpoint to accept it. Returns null unless all inputs are supplied.
 */
export function buildOptionalManualLaunchUrl({ tenantId, connectionId, fhirBaseUrl, launch, host = SMART_APP_HOST } = {}) {
  if (!tenantId || !connectionId || !fhirBaseUrl || !launch) return null;
  return `${trimHost(host)}${SMART_LAUNCH_PATH}`
    + `?tenantId=${encodeURIComponent(tenantId)}`
    + `&connectionId=${encodeURIComponent(connectionId)}`
    + `&iss=${encodeURIComponent(fhirBaseUrl)}`
    + `&launch=${encodeURIComponent(launch)}`;
}

/** Count ACTIVE / DISABLED mappings from the safe (redacted) mapping list. */
export function summarizeMappings(mappings) {
  const list = Array.isArray(mappings) ? mappings : [];
  let active = 0, disabled = 0;
  for (const m of list) {
    if (m && m.status === 'ACTIVE') active += 1;
    else if (m && m.status === 'DISABLED') disabled += 1;
  }
  return { total: list.length, active, disabled };
}

/**
 * Assemble the safe console model from a never-PHI connection doc + safe mapping
 * summaries. Includes only config values (no secrets/PHI/token/raw fhirUser).
 */
export function buildSandboxConsoleModel({ tenantId, connection, mappings, host = SMART_APP_HOST } = {}) {
  const c = connection || {};
  const connectionId = c.connectionId || null;
  const requireUserMapping = c.requireUserMapping === true;
  const mapping = summarizeMappings(mappings);
  return {
    tenantId: tenantId || null,
    connectionId,
    launchUrl: buildSmartLaunchUrl({ tenantId, connectionId, host }),
    redirectUri: buildSmartRedirectUri(host),
    scopes: SMART_REQUIRED_SCOPES,
    fhirBaseUrl: c.fhirBaseUrl || null,
    clientId: c.clientId || null,                 // public client id — safe to display
    status: c.status || null,
    sandboxMode: c.sandboxMode !== false,
    requireUserMapping,
    sourceSystemLabel: c.sourceSystemLabel || null,
    mapping,
    // Test-mode readiness signals (advisory only).
    baselineReady: requireUserMapping === false,
    enforcedReady: requireUserMapping === true && mapping.active > 0,
  };
}

/**
 * Pure activation-readiness decision for the EHR connection "Activate connection"
 * control. Returns whether the (client-visible) preconditions to OFFER activation
 * are met, plus a human-readable list of what is still missing. The SERVER gate
 * (ehrIntegrationConfig.assertActiveConnectionPreconditions + the production issuer
 * allowlist + platform_admin) remains the AUTHORITY — this only governs whether the
 * button is shown and the fail-fast hint. Never inspects PHI / secrets / raw fhirUser.
 *
 * All required to offer activation: connection is TESTING, last capability green (OK),
 * redirectUri equals the exact canonical callback, fhirBaseUrl present, requireUserMapping
 * is on, and >= 1 ACTIVE user mapping exists.
 *
 * @param {{ connection: object|null, mappings: Array, host?: string }} args
 * @returns {{ canActivate: boolean, isTesting: boolean, isActive: boolean, unmet: string[] }}
 */
export function computeEhrActivationReadiness({ connection, mappings, host = SMART_APP_HOST } = {}) {
  const c = connection && typeof connection === 'object' ? connection : null;
  const status = c ? c.status : null;
  const isTesting = status === 'TESTING';
  const isActive = status === 'ACTIVE';
  // Only a TESTING connection can be activated via this control.
  if (!c || !isTesting) return { canActivate: false, isTesting, isActive, unmet: [] };

  const capabilityGreen = typeof c.lastCapabilityStatus === 'string'
    && c.lastCapabilityStatus.toUpperCase() === 'OK';
  const { active } = summarizeMappings(mappings);

  const unmet = [];
  if (!capabilityGreen) unmet.push('a green capability test (Test capability → OK)');
  if ((c.redirectUri || '') !== buildSmartRedirectUri(host)) unmet.push('the exact canonical redirect URI');
  if (!c.fhirBaseUrl) unmet.push('a FHIR base URL');
  if (c.requireUserMapping !== true) unmet.push('“Require EHR user mapping” enabled');
  if (active < 1) unmet.push('at least one ACTIVE user mapping');

  return { canActivate: unmet.length === 0, isTesting: true, isActive: false, unmet };
}

// ── Checklists / guide (labels only; no live PHI reads) ───────────────────────

export const PASS_A_BASELINE = Object.freeze([
  'Connection status: TESTING',
  'requireUserMapping: false',
  'Capability test: green',
  'Launch from SMART sandbox',
  'Callback succeeds',
  'Packet is created',
  'Import candidates',
  'Continue to Gate',
  'EHR Suggestions appear',
  'Gate submit succeeds',
]);

export const PASS_B_ENFORCED = Object.freeze([
  'Confirm session has fhirUserHash',
  'Create mapping for GP',
  'Set requireUserMapping: true',
  'Launch again as mapped GP',
  'Mapped GP proceeds',
  'Unmapped GP blocks',
  'Wrong GP blocks',
]);

export const POST_LAUNCH_CHECKLIST = Object.freeze([
  'Launch session exists',
  'Session root has no raw token/patient/fhirUser',
  'Secure import material exists under launch session /phi path',
  'Packet root has sourceType ehr_launch',
  'Packet root has candidate count only',
  'Candidates exist under packet /phi/ehrImport/**',
  'Packet Inbox shows Continue to Referral Gate',
  'Gate opens with EHR Suggestions',
  'Referral root remains non-PHI after submit',
  'Referral /phi/** holds patient truth after submit',
]);

export const FAILURE_GUIDE = Object.freeze([
  { code: 'missing iss/launch', hint: 'External launcher did not call the launch URL correctly.' },
  { code: 'callback state error', hint: 'Launch session/state mismatch or expired.' },
  { code: 'IMPORT_MATERIAL_MISSING', hint: 'EHR_LAUNCH_IMPORT_KEY missing or callback did not stage material.' },
  { code: 'FHIR_USER_MISSING', hint: 'Sandbox did not return fhirUser while requireUserMapping is true.' },
  { code: 'USER_MAPPING_NOT_FOUND', hint: 'Create mapping before enabling requireUserMapping.' },
  { code: 'EHR_USER_MISMATCH', hint: 'Launch belongs to another mapped GP.' },
  { code: 'Gate handoff disabled', hint: 'Import not complete or packet unavailable.' },
]);

export const SMART_LAUNCHER_INSTRUCTIONS = Object.freeze([
  'Use Provider EHR Launch.',
  'Paste the Launch URL.',
  'Use the Redirect URI.',
  'Use the configured client_id.',
  'Request the listed scopes.',
  'Select a test patient and provider.',
  'Launch the app.',
]);

export default {
  SMART_APP_HOST,
  SMART_LAUNCH_PATH,
  SMART_CALLBACK_PATH,
  SMART_REQUIRED_SCOPES,
  SMART_APP_LAUNCHER_URL,
  canUseSandboxConsole,
  buildSmartLaunchUrl,
  buildSmartRedirectUri,
  buildOptionalManualLaunchUrl,
  summarizeMappings,
  buildSandboxConsoleModel,
  PASS_A_BASELINE,
  PASS_B_ENFORCED,
  POST_LAUNCH_CHECKLIST,
  FAILURE_GUIDE,
  SMART_LAUNCHER_INSTRUCTIONS,
};
